import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Loader from "../../Common/loader/Loader";
import {
  FiDownload,
  FiPlay,
  FiFileText,
  FiCompass,
  FiArrowRight,
} from "react-icons/fi";

import DOMPurify from "dompurify";
import { useAboutContext } from "../../context/About_context";
import {
  getEmbedUrl,
  getYouTubeId,
  getYouTubeThumbnail,
  DynamicIcon,
  renderTitleWithGradient,
} from "../../utils/helper";
import "./Exhibition.css";

/* ---------- Scroll Reveal Component (Framer Motion) ---------- */
const Reveal = ({ children, className = "", delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={{
        duration: 0.7,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Exhibition = () => {
  const { exhibition_data, GetExhibition, exhibition_loading } =
    useAboutContext();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    GetExhibition();
  }, []); //eslint-disable-line

  if (
    exhibition_loading &&
    (!exhibition_data || Object.keys(exhibition_data).length === 0)
  ) {
    return <Loader fullScreen={true} text="Loading Exhibition..." />;
  }

  return (
    <div className="exhibition-page">
      <Helmet>
        <title>Exhibition | Bharat Buildcon</title>
      </Helmet>
      {/* Background Soft Mesh Glow Orbs */}
      <div className="ex-bg-wrapper" aria-hidden="true">
        <div className="ex-orb ex-orb-1" />
        <div className="ex-orb ex-orb-2" />
        <div className="ex-orb ex-orb-3" />
      </div>

      <div className="ex-container">
        {/* Header & Hero Section */}
        <header className="ex-header">
          {/* Breadcrumbs Pill */}
          <div className="ex-breadcrumb-wrapper">
            <nav className="ex-breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="ex-bc-home">
                HOME
              </Link>
              <span className="ex-bc-slash">/</span>
              <span className="ex-bc-active">EXHIBITION</span>
            </nav>
          </div>

          {/* Title & Subtitle */}
          <Reveal delay={100}>
            {exhibition_data?.exhibition_title && (
              <h1 className="ex-title">
                {renderTitleWithGradient(exhibition_data.exhibition_title)}
              </h1>
            )}
            {exhibition_data?.exhibition_description && (
              <div
                className="ex-subtitle"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(exhibition_data.exhibition_description),
                }}
              />
            )}
          </Reveal>

          {/* Hero Widescreen Banner Card */}
          <Reveal delay={150}>
            <div className="ex-hero-card">
              {exhibition_data?.exhibition_image && (
                <img
                  src={exhibition_data.exhibition_image}
                  alt="Bharat Buildcon Exhibition Banner"
                  className="ex-hero-img"
                />
              )}
            </div>
          </Reveal>
        </header>

        {/* ---------------- SECTION 1: WHY EXHIBIT AT BHARAT BUILDCON? ---------------- */}
        {/* <section className="ex-section" id="why-exhibit">
          <Reveal delay={100}>
            <div className="ex-why-header">
              <span className="ex-why-tag">EXHIBITOR ADVANTAGE</span>

              {exhibition_data?.why_exhibition_title && (
                <h2 className="ex-why-title">
                  {renderTitleWithGradient(exhibition_data.why_exhibition_title)}
                </h2>
              )}
              {exhibition_data?.why_exhibition_description && (
                <div
                  className="ex-why-desc"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(exhibition_data.why_exhibition_description),
                  }}
                />
              )}
            </div>
          </Reveal>

          {Array.isArray(exhibition_data?.exhibition_advantages) &&
            exhibition_data.exhibition_advantages.length > 0 && (
              <Reveal delay={150}>
                <div className="ex-why-grid">
                  {exhibition_data.exhibition_advantages.map((pt, idx) => (
                    <div key={pt.id || idx} className="ex-why-card">
                      {pt.icon_name && (
                        <div className="ex-why-icon-wrap">
                          <DynamicIcon name={pt.icon_name} />
                        </div>
                      )}
                      {pt.advantage_title && (
                        <h3 className="ex-why-card-title">
                          {pt.advantage_title}
                        </h3>
                      )}
                      {pt.advantage_description && (
                        <div className="ex-why-card-desc">
                          {pt.advantage_description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
        </section> */}

        {/* ---------------- SECTION 2: OUR BROCHURES ---------------- */}
        {Array.isArray(exhibition_data?.exhibition_brochures) &&
          exhibition_data.exhibition_brochures.length > 0 && (
            <section className="ex-section" id="brochures">
              <Reveal delay={150}>
                <div className="ex-section-header center">
                  <span className="ex-pill-label">OFFICIAL DOCUMENTS</span>
                  <h2 className="ex-section-title">
                    {renderTitleWithGradient("Our Brochures")}
                  </h2>
                  <p className="ex-section-desc">
                    Download official event brochures and comprehensive guides
                    tailored for domestic and international participants.
                  </p>
                </div>
              </Reveal>

              <div className="ex-brochures-grid">
                {exhibition_data.exhibition_brochures.map((item, idx) => (
                  <Reveal
                    key={item.id || idx}
                    delay={200 + idx * 50}
                    className="ex-brochure-col"
                  >
                    <div className="ex-brochure-card">
                      <div className="ex-brochure-top">
                        {item.category && (
                          <span className="ex-brochure-badge">
                            {item.category}
                          </span>
                        )}
                        <div className="ex-brochure-icon-wrap">
                          <FiFileText />
                        </div>
                      </div>
                      {item.title && (
                        <h3 className="ex-brochure-title">{item.title}</h3>
                      )}
                      {item.description && (
                        <p className="ex-brochure-desc">{item.description}</p>
                      )}
                      {item.brochure_full_path && (
                        <div className="ex-brochure-action">
                          <a
                            href={item.brochure_full_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ex-btn-download"
                          >
                            <FiDownload /> DOWNLOAD BROCHURE
                          </a>
                        </div>
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>
            </section>
          )}

        {/* ---------------- SECTION 3: PLAN LAYOUT ---------------- */}
        <section className="ex-section" id="plan-layout">
          <Reveal delay={150}>
            <div className="ex-section-header center">
              <span className="ex-pill-label">FLOOR ARCHITECTURE</span>
              <h2 className="ex-section-title">
                {renderTitleWithGradient("Plan Layout")}
              </h2>
              <div
                className="ex-section-desc"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    exhibition_data?.plan_layout_description || "",
                  ),
                }}
              />
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="ex-layout-card">
              {/* Layout Visual Frame */}
              <div className="ex-layout-display">
                <div className="ex-layout-image-box">
                  {(exhibition_data?.plan_image_full_path ||
                    exhibition_data?.plan_image) && (
                    <img
                      src={
                        exhibition_data?.plan_image_full_path ||
                        exhibition_data?.plan_image
                      }
                      alt="Bharat Buildcon Floor Plan Layout Map"
                      className="ex-layout-img"
                    />
                  )}
                </div>

                {/* Exhibitor Enquiry Form Button */}
                <div className="ex-layout-action-wrap">
                  <Link to="/exhibitors" className="ex-btn-enquiry-pill">
                    EXHIBITOR ENQUIRY FORM <FiArrowRight />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ---------------- SECTION 4: ABOUT VENUE (YASHOBHOOMI) ---------------- */}
        <section className="ex-section" id="about-venue">
          <Reveal delay={100}>
            <div className="ex-section-header center">
              <span className="ex-pill-label">ABOUT VENUE</span>
              {exhibition_data?.venue_exhibition_title && (
                <h2 className="ex-section-title">
                  {renderTitleWithGradient(exhibition_data.venue_exhibition_title)}
                </h2>
              )}
              {exhibition_data?.venue_exhibition_description && (
                <div
                  className="ex-section-desc"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(exhibition_data.venue_exhibition_description),
                  }}
                />
              )}
            </div>
          </Reveal>

          {/* Venue Image Card */}
          <Reveal delay={150}>
            <div className="ex-venue-image-card">
              {exhibition_data?.venue_image && (
                <img
                  src={exhibition_data.venue_image}
                  alt="Yashobhoomi Convention Centre Venue"
                  className="ex-venue-img"
                />
              )}
            </div>
          </Reveal>

          {/* Card-Free Access Specs View */}
          {Array.isArray(exhibition_data?.exhibition_connectivity) &&
            exhibition_data.exhibition_connectivity.length > 0 && (
              <div className="ex-venue-specs-grid">
                {exhibition_data.exhibition_connectivity.map((item, idx) => (
                  <Reveal
                    key={item.id || idx}
                    delay={150 + idx * 30}
                    className="ex-vspec-col"
                  >
                    <div className="ex-vspec-item">
                      <div className="ex-vspec-icon-box">
                        <FiCompass />
                      </div>
                      <div className="ex-vspec-content">
                        {item.name && (
                          <h3 className="ex-vspec-title">{item.name}</h3>
                        )}
                        {item.description && (
                          <div className="ex-vspec-sub">{item.description}</div>
                        )}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
        </section>

        {/* ---------------- SECTION 5: VIDEO TOUR & MAP LOCATION ---------------- */}
        <section className="ex-section" id="venue-media">
          <div className="ex-media-grid">
            {/* Left: Video Showcase */}
            <Reveal delay={150} className="ex-media-col">
              <div className="ex-media-card video-card">
                <div className="ex-card-label">VENUE VIDEO TOUR</div>
                {exhibition_data?.exhibition_tour_title && (
                  <h3 className="ex-media-title">
                    {renderTitleWithGradient(exhibition_data.exhibition_tour_title)}
                  </h3>
                )}
                {exhibition_data?.exhibition_tour_description && (
                  <div
                    className="ex-media-desc"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(exhibition_data.exhibition_tour_description),
                    }}
                  />
                )}

                {exhibition_data?.venue_video_tour && (
                  <div className="ex-video-box">
                    {!isVideoPlaying ? (
                      <div className="ex-video-poster">
                        {getYouTubeId(exhibition_data.venue_video_tour) ? (
                          <img
                            src={getYouTubeThumbnail(
                              exhibition_data.venue_video_tour
                            )}
                            alt="Venue Tour Video Thumbnail"
                            className="ex-poster-video-thumb"
                          />
                        ) : (
                          <video
                            src={exhibition_data.venue_video_tour}
                            className="ex-poster-video-thumb"
                            muted
                            loop
                            playsInline
                          />
                        )}
                        <div className="ex-video-overlay">
                          <button
                            className="ex-play-btn"
                            onClick={() => setIsVideoPlaying(true)}
                            aria-label="Play Video"
                          >
                            <FiPlay />
                          </button>
                          <span className="ex-play-text">
                            WATCH VENUE SHOWCASE
                          </span>
                        </div>
                      </div>
                    ) : getYouTubeId(exhibition_data.venue_video_tour) ? (
                      <iframe
                        src={getEmbedUrl(exhibition_data.venue_video_tour)}
                        title="Venue Video Tour"
                        className="ex-active-video"
                        style={{ width: "100%", height: "100%", border: 0 }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={exhibition_data.venue_video_tour}
                        className="ex-active-video"
                        controls
                        autoPlay
                      />
                    )}
                  </div>
                )}
              </div>
            </Reveal>

            {/* Right: Interactive Location Map */}
            <Reveal delay={200} className="ex-media-col">
              <div className="ex-media-card map-card">
                <div className="ex-card-label">LOCATION & MAP</div>
                {exhibition_data?.exhibition_location_title && (
                  <h3 className="ex-media-title">
                    {renderTitleWithGradient(exhibition_data.exhibition_location_title)}
                  </h3>
                )}
                {exhibition_data?.exhibition_location_description && (
                  <div
                    className="ex-media-desc"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(exhibition_data.exhibition_location_description),
                    }}
                  />
                )}

                <div className="ex-map-box">
                  {exhibition_data?.contact_link && (
                    <iframe
                      title="Yashobhoomi Location Map"
                      src={exhibition_data.contact_link}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Exhibition;
